import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import CtaSection from '../components/sections/CtaSection';
import { OpenAIIcon, SlackIcon, SalesforceIcon, WooCommerceIcon, WebhookIcon, ApiIcon, EmailIcon } from '../components/ui/IntegrationIcons';
import SEO from '../components/SEO';
import { PRICING_WHATSAPP_URL } from '../config/contact';
import './FeaturesPage.css';

const HIGHLIGHTS = [
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>, title: '5 Channels', desc: 'WhatsApp, Email, Shopify, WooCommerce, LeadFlow. One dashboard.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>, title: 'Under 1s Reply', desc: 'AI responds faster than any human agent, 24 hours a day, every day.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>, title: 'Plain Text Training', desc: 'Upload your catalog, FAQs, policies. No code. The AI learns everything instantly.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>, title: 'Human Takeover', desc: 'One click to join any conversation. AI pauses, you reply, AI resumes.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/></svg>, title: 'AI Sales Pipeline', desc: 'Detects buying intent and moves leads through 6 stages automatically.' },
  { icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>, title: '50+ Integrations', desc: 'Shopify, WooCommerce, OpenAI, Zapier, HubSpot, Stripe and more.' },
];

const CHANNELS = [
  {
    id: 'whatsapp',
    badge: 'WhatsApp',
    color: '#25D366',
    title: 'Your 24/7 WhatsApp sales & support agent',
    desc: 'Zepliq connects to WhatsApp via a simple QR scan with no API approval and no developers needed. Once connected, your AI reads every incoming message, understands the context, and replies instantly with accurate, on-brand responses.',
    points: [
      'AI auto-reply trained on your products and FAQs',
      'Order confirmations for Shopify & WooCommerce',
      'Broadcast campaigns to custom segments',
      'Human takeover with one click',
      'Multi-agent team inbox',
      'Full conversation history and analytics',
    ],
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
    flip: false,
  },
  {
    id: 'email',
    badge: 'AI Email Assistant',
    color: '#EA4335',
    title: 'An AI assistant for your Gmail inbox',
    desc: 'Connect your Google account and Zepliq reads incoming email, drafts on-brand replies for your review, and sends the ones you approve, all powered by the same knowledge base as your WhatsApp automation.',
    points: [
      'Reads incoming Gmail so the assistant understands every message',
      'AI-drafted replies, ready for your review before anything is sent',
      'Send approved replies on your behalf in one click',
      'Auto-organize: mark messages as read and keep the inbox tidy',
      'Shared knowledge base across email and WhatsApp',
      'Human takeover on any thread, anytime',
    ],
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>,
    flip: true,
  },
  {
    id: 'ecommerce',
    badge: 'Shopify & WooCommerce',
    color: '#96BF48',
    title: 'Automate every order, from confirmation to cancellation',
    desc: 'Connect your Shopify or WooCommerce store in minutes. Every new order automatically triggers a WhatsApp confirmation to the customer. If they don\'t reply, configurable follow-ups go out. If they cancel, your store updates automatically.',
    points: [
      'Instant WhatsApp order confirmations on every purchase',
      'AI reads YES/NO replies and updates order status',
      'Configurable follow-ups at 30min, 2hr, 24hr intervals',
      'Auto-cancel orders after follow-up exhaustion',
      'Full trace log for every confirmation attempt',
      'Connect via OAuth (Shopify) or Webhooks (WooCommerce)',
    ],
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/></svg>,
    flip: false,
  },
  {
    id: 'leadflow',
    badge: 'LeadFlow Pipeline',
    color: '#6366F1',
    title: 'A full AI-powered CRM built into your chat',
    desc: 'Zepliq doesn\'t just reply to messages. It tracks every contact through a complete sales pipeline. The AI reads conversation signals and moves leads through stages automatically, or your team manages it manually from the Kanban board.',
    points: [
      '6-stage pipeline: New Lead → Interested → Follow-Up → Proposal → Negotiating → Client',
      'AI intent detection moves leads between stages automatically',
      'Kanban board with drag-and-drop for manual management',
      'Contact tags, deal values, notes, and custom fields',
      'Bulk CSV import for existing contacts',
      'Pipeline analytics: conversion rates, drop-offs, deal values',
    ],
    icon: <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="4" height="18"/><rect x="10" y="3" width="4" height="18"/><rect x="17" y="3" width="4" height="18"/></svg>,
    flip: true,
  },
];

const INTEGRATIONS = [
  { name: 'OpenAI',        Icon: OpenAIIcon,       color: '#10A37F' },
  { name: 'Google Gemini', slug: 'googlegemini',   color: '#4285F4' },
  { name: 'Claude AI',     slug: 'anthropic',      color: '#D4723A' },
  { name: 'Shopify',       slug: 'shopify',        color: '#96BF48' },
  { name: 'WooCommerce',   Icon: WooCommerceIcon,  color: '#7F54B3' },
  { name: 'Zapier',        slug: 'zapier',         color: '#FF4A00' },
  { name: 'HubSpot',       slug: 'hubspot',        color: '#FF7A59' },
  { name: 'Stripe',        slug: 'stripe',         color: '#635BFF' },
  { name: 'Google Sheets', slug: 'googlesheets',   color: '#34A853' },
  { name: 'Calendly',      slug: 'calendly',       color: '#006BFF' },
  { name: 'Salesforce',    Icon: SalesforceIcon,   color: '#00A1E0' },
  { name: 'Slack',         Icon: SlackIcon,        color: '#E01E5A' },
  { name: 'Webhooks',      Icon: WebhookIcon,      color: '#6366F1' },
  { name: 'REST API',      Icon: ApiIcon,          color: '#0EA5E9' },
  { name: 'SMTP Email',    Icon: EmailIcon,        color: '#EA4335' },
];

export default function FeaturesPage() {
  const canvasRef = useRef(null);
  const animRef   = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let W, H, particles = [];
    const PARTICLE_COUNT = 55;

    const resize = () => {
      W = canvas.width  = canvas.offsetWidth;
      H = canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    class Particle {
      constructor() { this.reset(); }
      reset() {
        this.x  = Math.random() * W;
        this.y  = Math.random() * H;
        this.r  = Math.random() * 1.8 + 0.4;
        this.dx = (Math.random() - 0.5) * 0.25;
        this.dy = (Math.random() - 0.5) * 0.25;
        this.o  = Math.random() * 0.5 + 0.15;
        this.hue = Math.random() > 0.6 ? 245 : 270;
      }
      update() {
        this.x += this.dx; this.y += this.dy;
        if (this.x < 0 || this.x > W || this.y < 0 || this.y > H) this.reset();
      }
      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.r, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${this.hue}, 80%, 55%, ${this.o})`;
        ctx.fill();
      }
    }

    for (let i = 0; i < PARTICLE_COUNT; i++) particles.push(new Particle());

    const drawLines = () => {
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const d  = Math.sqrt(dx * dx + dy * dy);
          if (d < 100) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(99,102,241,${0.06 * (1 - d / 100)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => { p.update(); p.draw(); });
      drawLines();
      animRef.current = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <div className="features-page">
      <SEO
        title="Features — WhatsApp AI Chatbot, AI Email Assistant & CRM"
        description="Explore all Zepliq features: AI WhatsApp chatbot, AI Email Assistant for Gmail, Shopify & WooCommerce order confirmations, lead generation, broadcast campaigns, and 24/7 auto-reply."
        keywords="WhatsApp chatbot features, AI chatbot capabilities, AI email assistant, Gmail automation, WhatsApp CRM features, order confirmation bot, lead generation chatbot, WhatsApp broadcast, auto reply WhatsApp"
        canonical="/features"
      />

      {/* ── HERO ── */}
      <section className="features-hero">
        <canvas ref={canvasRef} className="features-hero__canvas" aria-hidden="true" />
        <div className="features-hero__blob features-hero__blob--1" aria-hidden="true" />
        <div className="features-hero__blob features-hero__blob--2" aria-hidden="true" />
        <div className="container features-hero__inner">
          <span className="badge badge-brand">Platform Features</span>
          <h1 className="display-xl features-hero__title">
            One platform.<br />
            <span className="gradient-text">Every channel. Every tool.</span>
          </h1>
          <p className="text-lg features-hero__sub">
            AI automation for WhatsApp, Email, Shopify, WooCommerce, and a full sales pipeline, all from one dashboard.
          </p>
          <div className="features-hero__actions">
            <Link to="/contact" className="btn btn-primary btn-lg">
              Start Free
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
            </Link>
            <a href={PRICING_WHATSAPP_URL} className="btn btn-secondary btn-lg" target="_blank" rel="noopener noreferrer">Get Pricing on WhatsApp</a>
          </div>
        </div>
      </section>

      {/* ── HIGHLIGHT CARDS ── */}
      <section className="features-highlights section">
        <div className="container">
          <div className="features-highlights__intro">
            <span className="badge badge-accent">Everything you need</span>
            <p className="features-highlights__intro-title">Five channels. One dashboard. Zero complexity.</p>
            <p className="features-highlights__intro-sub">Everything your business needs to automate sales and support, built in.</p>
          </div>
          <div className="features-highlights__grid">
            {HIGHLIGHTS.map((h, i) => (
              <div key={i} className="features-highlights__card">
                <div className="features-highlights__icon">{h.icon}</div>
                <h3 className="features-highlights__title">{h.title}</h3>
                <p className="features-highlights__desc">{h.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHATSAPP SECTION ── */}
      <section id="whatsapp" className="ch-section section ch-section--surface">
        <div className="container">
          <div className="ch-section__inner">
            <div className="ch-section__copy">
              <span className="ch-section__badge" style={{ '--ch-color': '#25D366' }}>
                <span className="ch-section__badge-icon" style={{ color: '#25D366' }}>{CHANNELS[0].icon}</span>
                WhatsApp
              </span>
              <h2 className="display-lg ch-section__title">{CHANNELS[0].title}</h2>
              <p className="text-lg ch-section__desc">{CHANNELS[0].desc}</p>
              <ul className="ch-section__points">
                {CHANNELS[0].points.map((p, pi) => (
                  <li key={pi} className="ch-section__point">
                    <span className="ch-section__point-check">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>{p}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn btn-primary">Get started <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></Link>
            </div>
            <div className="ch-section__visual">
              <div className="ch-mockup-wa">
                <div className="ch-mockup__bar">
                  <span className="ch-mockup__dot" style={{ background: '#25D366' }} />
                  <span className="ch-mockup__bar-title">WhatsApp · AI Agent</span>
                  <span className="ch-mockup__status ch-mockup__status--on">Online</span>
                </div>
                <div className="ch-mockup__chat">
                  <div className="ch-bubble ch-bubble--in">Hi, is the navy hoodie available in size M?</div>
                  <div className="ch-bubble ch-bubble--out" style={{ '--bubble-color': '#25D366' }}>
                    Yes! The Navy Hoodie (M) is in stock — $29.99. Want me to send you the link? 🛒
                    <span className="ch-bubble__tick">
                      <svg width="14" height="10" viewBox="0 0 16 11" fill="none"><path d="M1 5.5L5 9.5L15 1.5" stroke="#25D366" strokeWidth="2" strokeLinecap="round"/><path d="M5 5.5L9 9.5" stroke="#25D366" strokeWidth="2" strokeLinecap="round"/></svg>
                    </span>
                  </div>
                  <div className="ch-bubble ch-bubble--in">Yes please!</div>
                  <div className="ch-bubble ch-bubble--out" style={{ '--bubble-color': '#25D366' }}>
                    Here you go 👉 zepliq.store/hoodie-navy-m — Free delivery on orders above $49!
                    <span className="ch-bubble__tick">
                      <svg width="14" height="10" viewBox="0 0 16 11" fill="none"><path d="M1 5.5L5 9.5L15 1.5" stroke="#25D366" strokeWidth="2" strokeLinecap="round"/><path d="M5 5.5L9 9.5" stroke="#25D366" strokeWidth="2" strokeLinecap="round"/></svg>
                    </span>
                  </div>
                </div>
                <div className="ch-mockup__footer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  AI replied in <strong>0.4s</strong> · Human takeover available
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── EMAIL SECTION ── */}
      <section id="email" className="ch-section section ch-section--light">
        <div className="container">
          <div className="ch-section__inner ch-section__inner--flip">
            <div className="ch-section__copy">
              <span className="ch-section__badge" style={{ '--ch-color': '#EA4335' }}>
                <span className="ch-section__badge-icon" style={{ color: '#EA4335' }}>{CHANNELS[1].icon}</span>
                AI Email Assistant
              </span>
              <h2 className="display-lg ch-section__title">{CHANNELS[1].title}</h2>
              <p className="text-lg ch-section__desc">{CHANNELS[1].desc}</p>
              <ul className="ch-section__points">
                {CHANNELS[1].points.map((p, pi) => (
                  <li key={pi} className="ch-section__point">
                    <span className="ch-section__point-check">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>{p}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn btn-primary">Get started <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></Link>
            </div>
            <div className="ch-section__visual">
              <div className="ch-mockup-wa">
                <div className="ch-mockup__bar">
                  <span className="ch-mockup__dot" style={{ background: '#EA4335' }} />
                  <span className="ch-mockup__bar-title">Gmail · AI Assistant</span>
                  <span className="ch-mockup__status ch-mockup__status--on">Draft ready</span>
                </div>
                <div className="ch-mockup__chat">
                  <div className="ch-bubble ch-bubble--in">Hi — do you ship to Canada, and what's the delivery time?</div>
                  <div className="ch-bubble ch-bubble--out" style={{ '--bubble-color': '#EA4335' }}>
                    Hi Jordan! Yes, we ship to Canada 🇨🇦 Standard delivery is 5–7 business days, and orders over $49 ship free. Want me to send a tracking link once it's dispatched?
                  </div>
                </div>
                <div className="ch-mockup__footer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z"/></svg>
                  AI drafted this reply · Awaiting your approval
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SHOPIFY / WOOCOMMERCE SECTION ── */}
      <section id="ecommerce" className="ch-section section ch-section--surface">
        <div className="container">
          <div className="ch-section__inner">
            <div className="ch-section__copy">
              <span className="ch-section__badge" style={{ '--ch-color': '#96BF48' }}>
                <span className="ch-section__badge-icon" style={{ color: '#96BF48' }}>{CHANNELS[2].icon}</span>
                Shopify &amp; WooCommerce
              </span>
              <h2 className="display-lg ch-section__title">{CHANNELS[2].title}</h2>
              <p className="text-lg ch-section__desc">{CHANNELS[2].desc}</p>
              <ul className="ch-section__points">
                {CHANNELS[2].points.map((p, pi) => (
                  <li key={pi} className="ch-section__point">
                    <span className="ch-section__point-check">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>{p}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn btn-primary">Get started <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></Link>
            </div>
            <div className="ch-section__visual">
              <div className="ch-mockup-order">
                <div className="ch-mockup__bar">
                  <span className="ch-mockup__dot" style={{ background: '#96BF48' }} />
                  <span className="ch-mockup__bar-title">Order Confirmation · WhatsApp</span>
                </div>
                <div className="ch-mockup__order-card">
                  <div className="ch-mockup__order-row">
                    <span className="ch-mockup__order-label">Order</span>
                    <span className="ch-mockup__order-val">#WC-4821</span>
                  </div>
                  <div className="ch-mockup__order-row">
                    <span className="ch-mockup__order-label">Customer</span>
                    <span className="ch-mockup__order-val">Ahmed Raza</span>
                  </div>
                  <div className="ch-mockup__order-row">
                    <span className="ch-mockup__order-label">Item</span>
                    <span className="ch-mockup__order-val">Blue Hoodie × 1</span>
                  </div>
                  <div className="ch-mockup__order-row">
                    <span className="ch-mockup__order-label">Total</span>
                    <span className="ch-mockup__order-val ch-mockup__order-val--bold">$29.99</span>
                  </div>
                </div>
                <div className="ch-mockup__chat">
                  <div className="ch-bubble ch-bubble--out" style={{ '--bubble-color': '#96BF48' }}>
                    Hi Ahmed! Your order #WC-4821 (Blue Hoodie) is confirmed ✅ Reply YES to confirm or NO to cancel.
                  </div>
                  <div className="ch-bubble ch-bubble--in">YES</div>
                  <div className="ch-bubble ch-bubble--out" style={{ '--bubble-color': '#96BF48' }}>
                    Great! Order confirmed ✅ Expected delivery: 3–5 business days.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── LEADFLOW SECTION ── */}
      <section id="leadflow" className="ch-section section ch-section--light">
        <div className="container">
          <div className="ch-section__inner ch-section__inner--flip">
            <div className="ch-section__copy">
              <span className="ch-section__badge" style={{ '--ch-color': '#6366F1' }}>
                <span className="ch-section__badge-icon" style={{ color: '#6366F1' }}>{CHANNELS[3].icon}</span>
                LeadFlow Pipeline
              </span>
              <h2 className="display-lg ch-section__title">{CHANNELS[3].title}</h2>
              <p className="text-lg ch-section__desc">{CHANNELS[3].desc}</p>
              <ul className="ch-section__points">
                {CHANNELS[3].points.map((p, pi) => (
                  <li key={pi} className="ch-section__point">
                    <span className="ch-section__point-check">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                    </span>{p}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn btn-primary">Get started <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></Link>
            </div>
            <div className="ch-section__visual">
              <div className="ch-mockup-crm">
                <div className="ch-mockup__bar">
                  <span className="ch-mockup__dot" style={{ background: '#6366F1' }} />
                  <span className="ch-mockup__bar-title">LeadFlow Pipeline</span>
                  <span className="ch-mockup__crm-count">12 active leads</span>
                </div>
                <div className="ch-mockup__crm-stages">
                  {[
                    { label: 'New Lead',    count: 4, color: '#94A3B8' },
                    { label: 'Interested',  count: 3, color: '#60A5FA' },
                    { label: 'Follow-Up',   count: 2, color: '#FBBF24' },
                    { label: 'Proposal',    count: 2, color: '#F97316' },
                    { label: 'Client',      count: 1, color: '#22C55E' },
                  ].map((stage) => (
                    <div key={stage.label} className="ch-mockup__crm-stage">
                      <div className="ch-mockup__crm-stage-top">
                        <span className="ch-mockup__crm-label">{stage.label}</span>
                        <span className="ch-mockup__crm-count-badge" style={{ background: stage.color }}>{stage.count}</span>
                      </div>
                      <div className="ch-mockup__crm-bar-wrap">
                        <div className="ch-mockup__crm-bar" style={{ width: `${(stage.count / 4) * 100}%`, background: stage.color }} />
                      </div>
                    </div>
                  ))}
                </div>
                <div className="ch-mockup__footer">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
                  AI moved 3 leads to next stage today
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── INTEGRATIONS GRID ── */}
      <section className="features-integ section">
        <div className="container">
          <div className="features-integ__header">
            <span className="badge badge-accent">Integrations</span>
            <h2 className="display-lg features-integ__title">
              Connects to your<br />
              <span className="gradient-text">entire stack.</span>
            </h2>
            <p className="text-lg features-integ__sub">
              Plug Zepliq into the tools you already use. No developer required.
            </p>
          </div>
          <div className="features-integ__grid">
            {INTEGRATIONS.map((item, i) => (
              <div key={i} className="features-integ__item" style={{ '--fi-color': item.color }}>
                <span className="features-integ__logo">
                  {item.Icon
                    ? <item.Icon size={20} />
                    : <img
                        src={`https://cdn.simpleicons.org/${item.slug}/${item.color.slice(1)}`}
                        alt={item.name}
                        width="20"
                        height="20"
                        loading="lazy"
                        onError={e => { e.target.style.display = 'none'; }}
                      />
                  }
                </span>
                <span className="features-integ__name">{item.name}</span>
              </div>
            ))}
          </div>
          <p className="features-integ__note">
            Plus REST API and Webhooks for any custom integration.
          </p>
        </div>
      </section>

      <CtaSection />
    </div>
  );
}
